import { INTENSITY_BLOCK_COLORS, intensityBlockTextClass } from "@bcc/components/ui";
import { Badge } from "@bcc/components/ui/badge";
import { EmptyValue } from "@bcc/components/ui/empty-value";
import { cn } from "@bcc/lib/utils";
import { generateTestId } from "@client-shared";
import type { WebStatus } from "@shared/services/pim/types";
import type { IntensityPosition } from "@shared/types/intensity";
import type { ColumnDef } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import { ImageOff } from "lucide-react";
import { useState } from "react";
import { type AdminCatalogueTableRow, matchesBadgeSelection, matchesStatusSelection, toFilterSelection } from "./admin-catalogue-rows";

export const ADMIN_PRODUCTS_COLUMN_IDS = {
  PRODUCT: "product",
  ITEM_NUMBER: "itemNumber",
  PARENT_ID: "parentId",
  ROASTER: "roaster",
  INTENSITY: "intensity",
  POSITION: "position",
  CHARACTER: "character",
  BADGES: "badges",
  WEB_STATUS: "webStatus",
} as const;

const COMPONENT = "AdminProductsColumns";

export const setAdminProductsCellTestId = (columnId: string, rowId: string): string => generateTestId(COMPONENT, `${columnId}-${rowId}`);

/** Every PIM web status, in the order the status filter lists them. */
export const ADMIN_WEB_STATUSES: readonly WebStatus[] = [
  "Visible for Sale",
  "Visible but not for Sale",
  "Not Visible",
  "Out of Stock",
  "Temporarily Out of Stock",
];

// Same palette as the roaster-facing Status cell (StatusCellMarkup); the two
// statuses that table folds into "Out of Stock" keep their own shade here.
const WEB_STATUS_BADGE_CLASSES: Record<WebStatus, string> = {
  "Visible for Sale": "bg-green-500/10 text-green-700 dark:text-green-400",
  "Not Visible": "bg-gray-500/10 text-gray-700 dark:text-gray-400",
  "Out of Stock": "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  "Temporarily Out of Stock": "bg-orange-500/10 text-orange-700 dark:text-orange-400",
  "Visible but not for Sale": "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400",
};

// Unlike the roaster-facing products table, the admin catalogue keeps every
// PIM status distinct so a manager can tell them apart.
const WEB_STATUS_LABEL_KEYS: Record<WebStatus, string> = {
  "Visible for Sale": "views.productsTable.status.published",
  "Not Visible": "views.productsTable.status.notVisible",
  "Out of Stock": "views.productsTable.status.outOfStock",
  "Temporarily Out of Stock": "admin.productCatalogue.status.temporarilyOutOfStock",
  "Visible but not for Sale": "admin.productCatalogue.status.visibleNotForSale",
};

export const webStatusLabel = (t: TFunction, status: WebStatus): string => {
  const label: unknown = t(WEB_STATUS_LABEL_KEYS[status]);
  return typeof label === "string" ? label : status;
};

// Read-only thumbnail: the catalogue has no image actions, so it is not a button.
function CatalogueThumbnail({ imageUrl, name }: { readonly imageUrl?: string; readonly name: string }) {
  const [failed, setFailed] = useState(false);
  if (!imageUrl || failed) {
    return (
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted">
        <ImageOff className="h-5 w-5 text-muted-foreground" />
      </div>
    );
  }
  return (
    <img
      src={imageUrl}
      alt={name}
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-12 w-12 shrink-0 rounded-lg border object-cover"
    />
  );
}

// Inline background, as the SKU import scale does, so the colour ramp survives
// the production Tailwind purge. Shared by the Intensity pill and Position circle.
const intensityColorStyle = (position: IntensityPosition) => ({ backgroundColor: INTENSITY_BLOCK_COLORS[position] });

const textOrDash = (value: string | undefined) => (value ? <span className="text-sm">{value}</span> : <EmptyValue />);

/** Width of the Product column; long coffee names wrap inside it. */
export const ADMIN_PRODUCTS_PRODUCT_COLUMN_SIZE = 320;

export const getAdminProductsColumns = (t: TFunction): ColumnDef<AdminCatalogueTableRow>[] => [
  {
    id: ADMIN_PRODUCTS_COLUMN_IDS.PRODUCT,
    accessorFn: (row) => row.title ?? "",
    size: ADMIN_PRODUCTS_PRODUCT_COLUMN_SIZE,
    header: t("admin.productCatalogue.columns.product"),
    cell: ({ row }) => (
      <div className="flex items-center gap-3" data-testid={setAdminProductsCellTestId(ADMIN_PRODUCTS_COLUMN_IDS.PRODUCT, row.original.id)}>
        <CatalogueThumbnail key={row.original.imageUrl} imageUrl={row.original.imageUrl} name={row.original.title ?? ""} />
        <div className="flex min-w-56 flex-col">
          {row.original.title ? <span className="text-sm font-medium">{row.original.title}</span> : <EmptyValue />}
          <span className="text-xs text-muted-foreground">{row.original.bagSize ?? "–"}</span>
        </div>
      </div>
    ),
  },
  {
    id: ADMIN_PRODUCTS_COLUMN_IDS.ITEM_NUMBER,
    accessorFn: (row) => row.itemNumber ?? "",
    header: t("admin.productCatalogue.columns.itemNumber"),
    cell: ({ row }) => <span className="font-mono text-sm">{row.original.itemNumber ?? <EmptyValue />}</span>,
  },
  {
    id: ADMIN_PRODUCTS_COLUMN_IDS.PARENT_ID,
    accessorFn: (row) => row.parentId ?? "",
    header: t("admin.productCatalogue.columns.parentId"),
    cell: ({ row }) => <span className="font-mono text-sm">{row.original.parentId ?? <EmptyValue />}</span>,
  },
  {
    id: ADMIN_PRODUCTS_COLUMN_IDS.ROASTER,
    accessorFn: (row) => row.vendorName ?? "",
    header: t("admin.productCatalogue.columns.roaster"),
    cell: ({ row }) => (
      <div className="flex flex-col" data-testid={setAdminProductsCellTestId(ADMIN_PRODUCTS_COLUMN_IDS.ROASTER, row.original.id)}>
        {row.original.vendorName ? <span className="text-sm font-medium">{row.original.vendorName}</span> : <EmptyValue />}
        {row.original.vendorId ? (
          <span className="text-xs text-muted-foreground">
            {t("admin.productCatalogue.vendorIdLabel", { vendorId: row.original.vendorId })}
          </span>
        ) : null}
      </div>
    ),
  },
  {
    id: ADMIN_PRODUCTS_COLUMN_IDS.INTENSITY,
    accessorFn: (row) => row.intensityPosition,
    sortUndefined: "last",
    header: t("admin.productCatalogue.columns.intensity"),
    cell: ({ row }) => {
      const { intensityPosition: position, intensityZone: zone } = row.original;
      if (position === undefined || zone === undefined) return <EmptyValue />;
      return (
        <Badge
          variant="secondary"
          className={intensityBlockTextClass(position)}
          style={intensityColorStyle(position)}
          testId={setAdminProductsCellTestId(ADMIN_PRODUCTS_COLUMN_IDS.INTENSITY, row.original.id)}
        >
          {t(`admin.productCatalogue.intensityZones.${zone}`)}
        </Badge>
      );
    },
  },
  {
    id: ADMIN_PRODUCTS_COLUMN_IDS.POSITION,
    accessorFn: (row) => row.intensityPosition,
    sortUndefined: "last",
    header: t("admin.productCatalogue.columns.position"),
    cell: ({ row }) => {
      const position = row.original.intensityPosition;
      if (position === undefined) return <EmptyValue />;
      return (
        <span
          className={cn(
            "inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium",
            intensityBlockTextClass(position)
          )}
          style={intensityColorStyle(position)}
          data-testid={setAdminProductsCellTestId(ADMIN_PRODUCTS_COLUMN_IDS.POSITION, row.original.id)}
        >
          {position}
        </span>
      );
    },
  },
  {
    id: ADMIN_PRODUCTS_COLUMN_IDS.CHARACTER,
    accessorFn: (row) => row.character ?? "",
    header: t("admin.productCatalogue.columns.character"),
    cell: ({ row }) => textOrDash(row.original.character),
  },
  {
    id: ADMIN_PRODUCTS_COLUMN_IDS.BADGES,
    accessorFn: (row) => row.badges.join(", "),
    filterFn: (row, _columnId, value) => matchesBadgeSelection(row.original.badges, toFilterSelection(value)),
    header: t("admin.productCatalogue.columns.badges"),
    cell: ({ row }) =>
      row.original.badges.length === 0 ? (
        <EmptyValue />
      ) : (
        <div className="flex flex-wrap gap-1">
          {row.original.badges.map((badge) => (
            <Badge
              key={badge}
              variant="secondary"
              testId={setAdminProductsCellTestId(ADMIN_PRODUCTS_COLUMN_IDS.BADGES, `${row.original.id}-${badge}`)}
            >
              {badge}
            </Badge>
          ))}
        </div>
      ),
  },
  {
    id: ADMIN_PRODUCTS_COLUMN_IDS.WEB_STATUS,
    accessorFn: (row) => row.webStatus ?? "",
    filterFn: (row, _columnId, value) => matchesStatusSelection(row.original.webStatus, toFilterSelection(value)),
    header: t("admin.productCatalogue.columns.webStatus"),
    cell: ({ row }) => {
      const status = row.original.webStatus;
      if (!status) return <EmptyValue />;
      return (
        <Badge
          variant="secondary"
          className={WEB_STATUS_BADGE_CLASSES[status]}
          testId={setAdminProductsCellTestId(ADMIN_PRODUCTS_COLUMN_IDS.WEB_STATUS, row.original.id)}
        >
          {webStatusLabel(t, status)}
        </Badge>
      );
    },
  },
];
